import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  Divider,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Skeleton
} from '@mui/material';
import { 
  Add as AddIcon, 
  Event as EventIcon, 
  LocationOn as LocationIcon, 
  AttachMoney as PriceIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { event as eventApi } from '../../utils/api';
import { toast } from 'react-toastify';
import CreateEventModal from './components/CreateEventModal';
import { TabPanel } from '../../components/shared/TabPanel';
import EventCard from './components/EventCard';

const EventsPage = () => {
  const { t } = useTranslation('events');
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await eventApi.getAll({ 
        includePrivate: true,
        showPast: true 
      });
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error(t('errors.fetchingEvents'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleViewEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    fetchEvents();
    toast.success(t('success.eventCreated'));
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    return events.filter(event => new Date(event.date) >= now);
  };

  const getPastEvents = () => {
    const now = new Date();
    return events.filter(event => new Date(event.date) < now);
  };

  const renderEventCard = (event) => {
    return (
      <Grid item xs={12} sm={6} md={4} key={event._id}>
        <EventCard event={event} onClick={handleViewEvent} />
      </Grid>
    );
  };

  const renderNoEvents = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {t('eventList.noEventsFound')}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => setIsCreateModalOpen(true)}
        sx={{ mt: 2 }}
      >
        {t('eventList.addFirstEvent')}
      </Button>
    </Box>
  );

  const renderSkeletonCard = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          <Skeleton 
            variant="rectangular" 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper'
            }}
          />
        </Box>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="70%" height={32} />
          </Box>
          
          <Box display="flex" alignItems="center" mb={1}>
            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="40%" height={24} />
          </Box>
          
          <Box display="flex" alignItems="center" mb={1}>
            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="60%" height={24} />
          </Box>

          <Box display="flex" gap={1} mt={2}>
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>
        </CardContent>
        <CardActions>
          <Skeleton variant="text" width={120} height={36} />
        </CardActions>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Skeleton variant="text" width={250} height={48} />
            <Box>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1, display: 'inline-block' }} />
              <Skeleton variant="rounded" width={150} height={40} sx={{ display: 'inline-block' }} />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rounded" width={300} height={48} />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <React.Fragment key={item}>
                {renderSkeletonCard()}
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t('events.addEvent')}
            </Button>
          </Box>
        </Box>

        <Tabs 
          value={currentTab} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
        >
          <Tab label={t('events.upcomingEvents')} />
          <Tab label={t('events.pastEvents')} />
        </Tabs>

        <Divider sx={{ mb: 3 }} />

        <Box>
          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={3}>
              {getUpcomingEvents().length > 0 
                ? getUpcomingEvents().map(renderEventCard) 
                : renderNoEvents()}
            </Grid>
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <Grid container spacing={3}>
              {getPastEvents().length > 0 
                ? getPastEvents().map(renderEventCard) 
                : renderNoEvents()}
            </Grid>
          </TabPanel>
        </Box>
      </Box>

      <CreateEventModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Container>
  );
};

export default EventsPage; 